import axios from 'axios'
import { envVars } from '@/env-vars'
import {
	LOCAL_STORAGE_ACCESS_TOKEN,
	LOCAL_STORAGE_REFRESH_TOKEN
} from '@/features/auth/constants.ts'
import { useAuthStore } from '@/features/auth/store/store.ts'

const axiosConfig = {
	baseURL: envVars.VITE_API_ENDPOINT,
	headers: {
		common: {
			Authorization: localStorage.getItem('access_token')
				? `Bearer ${localStorage.getItem('access_token')}`
				: false,
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest'
		},
		post: {
			'Access-Control-Allow-Origin': '*'
		}
	}
}

// create axios custom instance with custom config
const axiosInstance = axios.create(axiosConfig)

const attemptRefresh = async (refreshToken: string) => {
	const url = 'luminate/refresh'
	try {
		const { data } = await axiosInstance.post(url, {
			refresh_token: refreshToken
		})
		localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, data.access_token)
		localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, data.refresh_token)
		useAuthStore.setState(() => ({
			accessToken: data.access_token,
			refreshToken: data.refresh_token
		}))

		return { data }
	} catch (error) {
		if (axios.isAxiosError(error)) {
			localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
			localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
			useAuthStore.getState().reset()
		} else {
			console.error(error)
		}
	}
}

axiosInstance.interceptors.request.use((request) => {
	const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
	if (token) {
		request.headers.Authorization = `Bearer ${token}`
	}
	return request
})

const endpointToIgnore = ['/login']

axiosInstance.interceptors.response.use(
	(response) => {
		// pass response without change
		return response
	},
	async (error) => {
		// get error info
		const statusCode = error?.response?.status
		const originalRequest = error.config
		switch (statusCode) {
			case 401: {
				const refreshToken = useAuthStore.getState().refreshToken
				localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
				if (
					refreshToken &&
					!originalRequest._retry &&
					!endpointToIgnore.includes(error.config.url)
				) {
					originalRequest._retry = true // prevent infinite retry loop
					await attemptRefresh(refreshToken)
					return axiosInstance.request(originalRequest)
				}
				localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
				useAuthStore.getState().reset()
				return Promise.reject(error)
			}
			default:
				return Promise.reject(error)
		}
	}
)

export default axiosInstance
